(() => {
  const root = document.querySelector("[data-journal-index], [data-journal-article]");
  if (!root || root.dataset.journalReady === "true") return;
  root.dataset.journalReady = "true";

  const index = root.matches("[data-journal-index]") ? root : null;
  const article = root.matches("[data-journal-article]") ? root : null;
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const message = (key, fallback) => window.DORIMM_I18N?.[key] || fallback;

  if (index) {
    const buttons = [...index.querySelectorAll("[data-journal-filter]")];
    const cards = [...index.querySelectorAll("[data-journal-card]")];
    const empty = index.querySelector("[data-journal-empty]");

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const filter = button.dataset.journalFilter;
        buttons.forEach((item) => {
          const active = item === button;
          item.classList.toggle("is-active", active);
          item.setAttribute("aria-pressed", String(active));
        });

        const visible = cards.filter((card) => filter === "all" || card.dataset.journalTopic === filter);
        cards.forEach((card) => { card.hidden = !visible.includes(card); });
        if (empty) empty.hidden = visible.length > 0;

        if (!reduceMotion && window.gsap && visible.length) {
          window.gsap.fromTo(visible, { autoAlpha: 0, y: 18 }, {
            autoAlpha: 1,
            y: 0,
            duration: .48,
            stagger: .055,
            ease: "power3.out",
            clearProps: "transform,opacity,visibility",
          });
        }
        window.ScrollTrigger?.refresh();
      });
    });
  }

  if (article) {
    const body = article.querySelector("[data-article-body]");
    const toc = article.querySelector("[data-article-toc]");
    const proseLanguage = (document.documentElement.lang || "en").toLowerCase().split("-")[0];
    const proseLabels = ({
      de: { fieldNote: "FELDNOTIZ", dataSet: "DATENSATZ", figure: "ABBILDUNG" },
      fr: { fieldNote: "NOTE TECHNIQUE", dataSet: "DONNÉES", figure: "FIGURE" },
      es: { fieldNote: "NOTA TÉCNICA", dataSet: "DATOS", figure: "FIGURA" },
      ja: { fieldNote: "技術ノート", dataSet: "比較データ", figure: "図" },
      ru: { fieldNote: "ТЕХНИЧЕСКАЯ ЗАМЕТКА", dataSet: "ДАННЫЕ", figure: "РИСУНОК" },
      zh: { fieldNote: "技術說明", dataSet: "比較資料", figure: "圖示" },
    })[proseLanguage] || { fieldNote: "FIELD NOTE", dataSet: "DATA SET", figure: "FIGURE" };

    /* Universal prose enhancement for existing and future Journal articles. */
    if (body && body.dataset.proseEnhanced !== "true") {
      body.dataset.proseEnhanced = "true";
      const sourceNodes = [...body.children];
      const lead = document.createElement("div");
      lead.className = "journal-prose-lead";
      let currentSection = null;
      let sectionCount = 0;

      sourceNodes.forEach((node) => {
        if (node.tagName === "H2") {
          sectionCount += 1;
          currentSection = document.createElement("section");
          currentSection.className = "journal-prose-section";
          currentSection.dataset.sectionIndex = String(sectionCount).padStart(2, "0");
          node.dataset.sectionIndex = currentSection.dataset.sectionIndex;
          node.dataset.sectionLabel = proseLabels.fieldNote;
          body.appendChild(currentSection);
          currentSection.appendChild(node);
        } else if (currentSection) {
          currentSection.appendChild(node);
        } else {
          lead.appendChild(node);
        }
      });

      if (lead.children.length) body.prepend(lead);

      const proseSections = [...body.querySelectorAll(":scope > .journal-prose-section")];
      proseSections.forEach((section, index) => {
        const title = (section.querySelector(":scope > h2")?.textContent || "").toLowerCase();
        if (index === proseSections.length - 1) section.classList.add("is-conclusion-section");
        if (/\bfaq\b|frequently asked|常见问题|よくある質問|häufige fragen|questions fréquentes|preguntas frecuentes|частые вопросы/i.test(title)) section.classList.add("is-faq-section");
        if (/claim|myth|误区|主張|behaupt|affirmation|afirmación|утвержден/i.test(title)) section.classList.add("is-claims-section");
      });

      body.querySelectorAll("aside").forEach((aside) => {
        aside.classList.add("journal-prose-callout");
        const label = aside.getAttribute("aria-label");
        if (label) aside.dataset.calloutLabel = label;
      });
      body.querySelectorAll("figure").forEach((figure) => {
        figure.classList.add("journal-prose-figure");
        figure.dataset.figureLabel = proseLabels.figure;
      });

      body.querySelectorAll("table").forEach((table) => {
        table.classList.add("journal-technical-table");
        const headers = [...table.querySelectorAll("thead th")].map((th) => th.textContent.trim());
        table.querySelectorAll("tbody tr").forEach((row, rowIndex) => {
          row.dataset.rowIndex = String(rowIndex + 1).padStart(2, "0");
          [...row.children].forEach((cell, cellIndex) => { if (headers[cellIndex]) cell.dataset.label = headers[cellIndex]; });
        });
        if (!table.parentElement?.classList.contains("journal-table-scroll")) {
          const scroll = document.createElement("div");
          scroll.className = "journal-table-scroll";
          table.parentNode.insertBefore(scroll, table);
          scroll.appendChild(table);
        }
        const tableSection = table.closest("section[aria-label]");
        if (tableSection) {
          tableSection.classList.add("journal-table-panel");
          tableSection.dataset.tableLabel = tableSection.getAttribute("aria-label") || message("technicalComparison", "Technical comparison");
          tableSection.dataset.tablePrefix = proseLabels.dataSet;
        }
      });

      body.querySelectorAll(".is-claims-section").forEach((section) => {
        const children = [...section.children].slice(1);
        const grid = document.createElement("div");
        grid.className = "journal-claim-grid";
        let card = null;
        children.forEach((node) => {
          if (node.tagName === "H3") {
            card = document.createElement("article");
            card.className = "journal-claim-card";
            card.dataset.claimIndex = String(grid.children.length + 1).padStart(2, "0");
            grid.appendChild(card);
          }
          (card || section).appendChild(node);
        });
        if (grid.children.length) section.appendChild(grid);
      });

      body.querySelectorAll(".is-faq-section").forEach((section) => {
        const nodes = [...section.children].slice(1);
        let details = null;
        nodes.forEach((node) => {
          if (node.tagName === "H3") {
            details = document.createElement("details");
            details.className = "journal-faq-item";
            const summary = document.createElement("summary");
            const question = document.createElement("span");
            const icon = document.createElement("i");
            question.textContent = node.textContent;
            icon.setAttribute("aria-hidden", "true");
            summary.append(question, icon);
            details.appendChild(summary);
            section.appendChild(details);
            node.remove();
          } else if (details) {
            let answer = details.querySelector(".journal-faq-answer");
            if (!answer) {
              answer = document.createElement("div");
              answer.className = "journal-faq-answer";
              details.appendChild(answer);
            }
            answer.appendChild(node);
          }
        });
      });
    }

    const headings = body ? [...body.querySelectorAll(":scope > .journal-prose-section > h2, :scope > .journal-prose-section h3")] : [];
    const slugCounts = new Map();

    headings.forEach((heading, index) => {
      const base = (heading.textContent || `section-${index + 1}`)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\u00c0-\u024f\u3040-\u30ff\u3400-\u9fff]+/g, "-")
        .replace(/^-|-$/g, "") || `section-${index + 1}`;
      const seen = slugCounts.get(base) || 0;
      slugCounts.set(base, seen + 1);
      heading.id = heading.id || `${base}${seen ? `-${seen + 1}` : ""}`;

      if (toc && heading.tagName === "H2") {
        const link = document.createElement("a");
        link.href = `#${heading.id}`;
        link.dataset.index = String(toc.children.length + 1).padStart(2, "0");
        link.textContent = heading.textContent;
        toc.appendChild(link);
      }
    });

    const tocLinks = toc ? [...toc.querySelectorAll("a")] : [];
    const h2s = headings.filter((heading) => heading.tagName === "H2");
    if ("IntersectionObserver" in window && h2s.length) {
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (!visible) return;
        tocLinks.forEach((link) => link.classList.toggle("is-active", link.hash === `#${visible.target.id}`));
      }, { rootMargin: "-18% 0px -68% 0px", threshold: 0 });
      h2s.forEach((heading) => observer.observe(heading));
    }

    const progress = article.querySelector("[data-article-progress]");
    const updateProgress = () => {
      if (!progress || !body) return;
      const rect = body.getBoundingClientRect();
      const distance = Math.max(1, body.offsetHeight - window.innerHeight * .35);
      const value = Math.max(0, Math.min(1, (window.innerHeight * .28 - rect.top) / distance));
      progress.style.transform = `scaleX(${value})`;
    };
    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress, { passive: true });

    const copyButton = article.querySelector("[data-copy-article]");
    copyButton?.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        const original = copyButton.firstChild?.textContent || message("copyLink", "COPY LINK");
        if (copyButton.firstChild) copyButton.firstChild.textContent = `${message("copied", "COPIED")} `;
        window.setTimeout(() => { if (copyButton.firstChild) copyButton.firstChild.textContent = original; }, 1600);
      } catch (_) {
        const fallback = document.createElement("input");
        fallback.type = "text";
        fallback.value = window.location.href;
        fallback.readOnly = true;
        fallback.className = "article-copy-fallback";
        fallback.setAttribute("aria-label", message("copyArticleLink", "Copy article link"));
        copyButton.insertAdjacentElement("afterend", fallback);
        fallback.select();
        copyButton.hidden = true;
      }
    });
  }

  if (reduceMotion || !window.gsap || !window.ScrollTrigger) return;
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);

  const mm = gsap.matchMedia();
  mm.add({ desktop: "(min-width: 821px)", reduce: "(prefers-reduced-motion: reduce)" }, (context) => {
    if (context.conditions.reduce) return;

    const hero = root.querySelector("[data-journal-hero]");
    if (hero) {
      const heroItems = hero.querySelectorAll(".journal-masthead__rail, .journal-masthead__title > *, .journal-masthead__intro > *, .article-breadcrumb, .article-hero__meta, .article-hero h1, .article-hero__deck");
      gsap.fromTo(heroItems, { y: 26, autoAlpha: 0 }, {
        y: 0,
        autoAlpha: 1,
        duration: .78,
        stagger: .055,
        ease: "power3.out",
        clearProps: "transform,opacity,visibility",
      });
    }

    const featureImage = root.querySelector("[data-journal-feature] img");
    if (featureImage) {
      gsap.to(featureImage, { yPercent: 5, ease: "none", scrollTrigger: { trigger: featureImage.closest("[data-journal-feature]"), start: "top bottom", end: "bottom top", scrub: .8 } });
    }

    const leadImage = root.querySelector("[data-article-media] img");
    if (leadImage) {
      gsap.to(leadImage, { yPercent: 4, ease: "none", scrollTrigger: { trigger: leadImage.closest("[data-article-media]"), start: "top bottom", end: "bottom top", scrub: .85 } });
    }

    ScrollTrigger.batch(root.querySelectorAll("[data-journal-card], .journal-prose-lead, .journal-prose-section, .article-related article"), {
      start: "top 88%",
      once: true,
      interval: .08,
      batchMax: 4,
      onEnter: (batch) => gsap.fromTo(batch, { y: 24, autoAlpha: 0 }, {
        y: 0,
        autoAlpha: 1,
        duration: .64,
        stagger: .07,
        ease: "power3.out",
        clearProps: "transform,opacity,visibility",
      }),
    });
  });

  window.addEventListener("load", () => ScrollTrigger.refresh(), { once: true });
})();
